import { BRIDGE_CONFIG } from "@bezoned/desktop-bridge/config.js";
import { BRIDGE_EVENTS } from "@bezoned/desktop-bridge/events.js";
import type {
  BinaryMessagePayload,
  FrontendRejectedPayload,
  InstanceInfo,
  JsonMessagePayload,
  RoomBroadcastPayload,
  RoomPayload,
} from "@bezoned/desktop-bridge/events.js";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";

type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "syncing"
  | "ready"
  | "waiting"
  | "reconnecting"
  | "disconnected";

interface DesktopBridgeListeners {
  onStatusChange?: (status: ConnectionStatus) => void;
  onInstanceListChange?: (instances: InstanceInfo[]) => void;
  onJsonMessage?: (payload: JsonMessagePayload) => void;
  onBinaryMessage?: (payload: BinaryMessagePayload) => void;
  onRoomBroadcast?: (payload: RoomBroadcastPayload) => void;
  onInstanceIdChange?: (instanceId: string) => void;
}

interface BridgeAuthTokenResponse {
  bridgeToken: string;
}

const HEARTBEAT_INTERVAL_MS = 5000;
const MAX_PENDING_JSON_MESSAGES_PER_ROOM = 100;
const BROWSER_INSTANCE_PREFIX = "teams-tab";
const INSTANCE_ID_RANDOM_RADIX = 16;
const BRIDGE_ORIGIN = `http://${BRIDGE_CONFIG.HOST}:${String(BRIDGE_CONFIG.PORT)}`;
const ignorePromiseRejection = (): void => {
  // bridge retries and status transitions are handled elsewhere
};

const createClientInstanceId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `${BROWSER_INSTANCE_PREFIX}-${globalThis.crypto.randomUUID()}`;
  }

  return `${BROWSER_INSTANCE_PREFIX}-${Date.now()}-${Math.random().toString(INSTANCE_ID_RANDOM_RADIX).slice(2)}`;
};

type WindowWithBezoned = typeof window & {
  bezoned?: {
    getBridgeAuthToken: () => Promise<string | null>;
  };
};

const isBridgeAuthTokenResponse = (
  value: unknown
): value is BridgeAuthTokenResponse => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  return "bridgeToken" in value && typeof value.bridgeToken === "string";
};

const createFetchAbortController = (): {
  controller: AbortController;
  cleanup: () => void;
} => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, BRIDGE_CONFIG.AUTH_TOKEN_REQUEST_TIMEOUT_MS);

  return {
    cleanup: () => {
      window.clearTimeout(timeoutId);
    },
    controller,
  };
};

export class DesktopBridgeClient {
  private socket: Socket | null = null;
  private listeners: DesktopBridgeListeners = {};
  private readonly desiredRooms = new Set<string>();
  private readonly joinedRooms = new Set<string>();
  private readonly pendingJsonByRoom = new Map<string, JsonMessagePayload[]>();
  private readonly instanceId = createClientInstanceId();
  private connectAttemptId = 0;

  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private removeVisibilityListener: (() => void) | null = null;
  private connectRetryTimeout: ReturnType<typeof setTimeout> | null = null;

  private appType: InstanceInfo["appType"] | null = null;
  private label: string | null = null;
  private bridgeToken: string | null = null;
  private status: ConnectionStatus = "idle";
  private retryDelayMs: number = BRIDGE_CONFIG.RECONNECT_DELAY;
  private shouldReconnect = false;

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  get id(): string {
    return this.instanceId;
  }

  setListeners(listeners: DesktopBridgeListeners): void {
    this.listeners = listeners;
    this.listeners.onInstanceIdChange?.(this.instanceId);
    this.listeners.onStatusChange?.(this.status);
  }

  connect(appType: InstanceInfo["appType"], label: string): void {
    if (this.socket) {
      return;
    }

    this.shouldReconnect = true;
    this.appType = appType;
    this.label = label;
    this.clearConnectRetryTimeout();
    this.startConnectAttempt("connecting");
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.connectAttemptId += 1;
    this.clearConnectRetryTimeout();
    this.stopHeartbeat();
    const { socket } = this;
    this.socket = null;
    socket?.removeAllListeners();
    socket?.io.removeAllListeners();
    socket?.disconnect();
    this.appType = null;
    this.label = null;
    this.bridgeToken = null;
    this.retryDelayMs = BRIDGE_CONFIG.RECONNECT_DELAY;
    this.joinedRooms.clear();
    this.pendingJsonByRoom.clear();
    this.setStatus("idle");
  }

  sendJson(room: string, data: unknown): void {
    const payload: JsonMessagePayload = { data, room };
    if (!this.socket?.connected) {
      return;
    }

    if (this.joinedRooms.has(room)) {
      this.socket.emit(BRIDGE_EVENTS.JSON_MESSAGE, payload);
      return;
    }

    if (!this.desiredRooms.has(room)) {
      return;
    }

    const queue = this.pendingJsonByRoom.get(room) ?? [];
    queue.push(payload);
    if (queue.length > MAX_PENDING_JSON_MESSAGES_PER_ROOM) {
      queue.shift();
    }
    this.pendingJsonByRoom.set(room, queue);
  }

  sendBinary(
    room: string,
    data: ArrayBuffer,
    mimeType: string,
    filename?: string
  ): void {
    if (this.status === "waiting") {
      return;
    }

    const payload: BinaryMessagePayload = filename
      ? { data, filename, mimeType, room }
      : { data, mimeType, room };

    this.socket?.emit(BRIDGE_EVENTS.BINARY_MESSAGE, payload);
  }

  joinRoom(room: string): void {
    this.desiredRooms.add(room);

    if (this.socket?.connected && this.status !== "waiting") {
      this.socket.emit(BRIDGE_EVENTS.ROOM_JOIN, { room } satisfies RoomPayload);
      this.sendHeartbeat();
    }
  }

  leaveRoom(room: string): void {
    this.desiredRooms.delete(room);
    this.joinedRooms.delete(room);
    this.pendingJsonByRoom.delete(room);

    if (this.socket?.connected && this.status !== "waiting") {
      this.socket.emit(BRIDGE_EVENTS.ROOM_LEAVE, {
        room,
      } satisfies RoomPayload);
      this.sendHeartbeat();
    }
  }

  broadcast(room: string, data: unknown): void {
    if (this.status === "waiting") {
      return;
    }

    const payload: RoomBroadcastPayload = { data, room };
    this.socket?.emit(BRIDGE_EVENTS.ROOM_BROADCAST, payload);
  }

  requestInstanceList(): void {
    this.socket?.emit(BRIDGE_EVENTS.INSTANCE_LIST_REQUEST);
  }

  private startConnectAttempt(status: "connecting" | "reconnecting"): void {
    if (!(this.appType && this.label)) {
      this.setStatus("disconnected");
      return;
    }

    const attemptId = ++this.connectAttemptId;
    this.setStatus(status);
    this.connectInternal(attemptId).catch(ignorePromiseRejection);
  }

  private isConnectAttemptActive(attemptId: number): boolean {
    return this.connectAttemptId === attemptId;
  }

  private async connectInternal(attemptId: number): Promise<void> {
    if (!(this.appType && this.label)) {
      if (!this.isConnectAttemptActive(attemptId)) {
        return;
      }
      this.setStatus("disconnected");
      return;
    }

    const { appType } = this;
    const { label } = this;

    const token = await this.fetchBridgeAuthToken();
    if (!this.isConnectAttemptActive(attemptId)) {
      return;
    }

    if (!token) {
      this.bridgeToken = null;
      const shouldReconnect = Boolean(this.shouldReconnect);
      this.setStatus(shouldReconnect ? "reconnecting" : "disconnected");
      if (shouldReconnect) {
        this.scheduleReconnectAttempt(attemptId);
      }
      return;
    }

    this.bridgeToken = token;

    const socket = io(BRIDGE_ORIGIN, {
      auth: (callback) => {
        callback({
          bridgeToken: this.bridgeToken,
        });
      },
      autoConnect: false,
      randomizationFactor: BRIDGE_CONFIG.RECONNECT_RANDOMIZATION_FACTOR,
      reconnection: false,
      reconnectionDelay: BRIDGE_CONFIG.RECONNECT_DELAY,
      reconnectionDelayMax: BRIDGE_CONFIG.RECONNECT_MAX_DELAY,
      // Polling avoids browser-native WebSocket errors when the optional
      // desktop app is not running.
      transports: ["polling"],
    });

    if (!this.isConnectAttemptActive(attemptId)) {
      socket.disconnect();
      return;
    }

    this.socket = socket;
    this.registerSocketHandlers(socket, appType, label, attemptId);
    socket.connect();
  }

  private registerSocketHandlers(
    socket: Socket,
    appType: InstanceInfo["appType"],
    label: string,
    attemptId: number
  ): void {
    const isStaleSocket = () =>
      !this.isConnectAttemptActive(attemptId) || this.socket !== socket;

    socket.on("connect", () => {
      if (isStaleSocket()) {
        socket.disconnect();
        return;
      }

      this.clearConnectRetryTimeout();
      this.retryDelayMs = BRIDGE_CONFIG.RECONNECT_DELAY;
      this.joinedRooms.clear();
      this.setStatus("connected");
      this.setStatus("syncing");
      this.emitInstanceRegister(socket, appType, label);
      this.rejoinDesiredRooms();
      this.startHeartbeat();
    });

    socket.on("disconnect", () => {
      if (isStaleSocket()) {
        return;
      }

      this.stopHeartbeat();
      this.joinedRooms.clear();
      this.pendingJsonByRoom.clear();
      this.restartConnectionAttempt(socket, attemptId);
    });

    socket.on("connect_error", (error) => {
      if (isStaleSocket()) {
        return;
      }

      this.stopHeartbeat();
      this.joinedRooms.clear();
      this.pendingJsonByRoom.clear();

      if (error.message.includes("bridge-auth-failed")) {
        console.info(
          "[DesktopBridge] Desktop app connection requires a fresh token; retrying"
        );
        this.restartConnectionAttempt(socket, attemptId, 0);
        return;
      }

      console.info(
        "[DesktopBridge] Desktop app is not running; retrying connection"
      );
      this.restartConnectionAttempt(socket, attemptId);
    });

    socket.on(BRIDGE_EVENTS.INSTANCE_REGISTER, (data: { id: string }) => {
      if (isStaleSocket()) {
        return;
      }

      if (data.id === this.instanceId) {
        this.setStatus("ready");
        return;
      }

      this.shouldReconnect = false;
      this.setStatus("disconnected");
      this.disposeSocket(socket);
    });

    socket.on(
      BRIDGE_EVENTS.FRONTEND_REJECTED,
      (payload: FrontendRejectedPayload) => {
        if (isStaleSocket()) {
          return;
        }

        if (payload.reason !== "another-instance-active") {
          return;
        }

        this.stopHeartbeat();
        this.joinedRooms.clear();
        this.setStatus("waiting");
      }
    );

    socket.on(BRIDGE_EVENTS.FRONTEND_ACCEPTED, () => {
      if (isStaleSocket()) {
        return;
      }

      this.setStatus("connected");
      this.setStatus("syncing");
      this.emitInstanceRegister(socket, appType, label);
      this.rejoinDesiredRooms();
      this.startHeartbeat();
    });

    socket.on(BRIDGE_EVENTS.ROOM_JOINED, (payload: RoomPayload) => {
      if (isStaleSocket()) {
        return;
      }

      this.joinedRooms.add(payload.room);
      this.flushPendingJson(payload.room);
      this.sendHeartbeat();
    });

    socket.on(BRIDGE_EVENTS.INSTANCE_LIST, (instances: InstanceInfo[]) => {
      if (isStaleSocket()) {
        return;
      }

      this.listeners.onInstanceListChange?.(instances);
    });

    socket.on(BRIDGE_EVENTS.JSON_MESSAGE, (payload: JsonMessagePayload) => {
      if (isStaleSocket()) {
        return;
      }

      this.listeners.onJsonMessage?.(payload);
    });

    socket.on(BRIDGE_EVENTS.BINARY_MESSAGE, (payload: BinaryMessagePayload) => {
      if (isStaleSocket()) {
        return;
      }

      this.listeners.onBinaryMessage?.(payload);
    });

    socket.on(BRIDGE_EVENTS.ROOM_BROADCAST, (payload: RoomBroadcastPayload) => {
      if (isStaleSocket()) {
        return;
      }

      this.listeners.onRoomBroadcast?.(payload);
    });
  }

  private rejoinDesiredRooms(): void {
    if (!this.socket?.connected || this.status === "waiting") {
      return;
    }

    this.joinedRooms.clear();

    for (const room of this.desiredRooms) {
      this.socket.emit(BRIDGE_EVENTS.ROOM_JOIN, { room } satisfies RoomPayload);
    }
  }

  private flushPendingJson(room: string): void {
    if (!(this.socket?.connected && this.joinedRooms.has(room))) {
      return;
    }

    const pending = this.pendingJsonByRoom.get(room);
    if (!pending || pending.length === 0) {
      return;
    }

    this.pendingJsonByRoom.delete(room);
    for (const payload of pending) {
      this.socket.emit(BRIDGE_EVENTS.JSON_MESSAGE, payload);
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) {
      return;
    }

    this.status = status;
    this.listeners.onStatusChange?.(status);
  }

  private async fetchBridgeAuthToken(): Promise<string | null> {
    const bezonedWindow = window as WindowWithBezoned;
    if (typeof bezonedWindow.bezoned?.getBridgeAuthToken === "function") {
      try {
        return await bezonedWindow.bezoned.getBridgeAuthToken();
      } catch {
        return null;
      }
    }

    const authTokenUrl = `${BRIDGE_ORIGIN}${BRIDGE_CONFIG.AUTH_TOKEN_PATH}`;
    const { controller, cleanup } = createFetchAbortController();

    try {
      const response = await fetch(authTokenUrl, {
        cache: "no-store",
        method: "GET",
        signal: controller.signal,
      });

      if (!response.ok) {
        return null;
      }

      const payload: unknown = await response.json();
      if (!isBridgeAuthTokenResponse(payload)) {
        return null;
      }

      return payload.bridgeToken;
    } catch {
      return null;
    } finally {
      cleanup();
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.sendHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    const visibilityListener = () => {
      this.sendHeartbeat();
    };

    document.addEventListener("visibilitychange", visibilityListener);
    this.removeVisibilityListener = () => {
      document.removeEventListener("visibilitychange", visibilityListener);
    };
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    this.removeVisibilityListener?.();
    this.removeVisibilityListener = null;
  }

  private sendHeartbeat(): void {
    if (!this.socket?.connected || this.status === "waiting") {
      return;
    }

    const visible = document.visibilityState === "visible";

    this.socket.emit(BRIDGE_EVENTS.INSTANCE_HEARTBEAT, {
      rooms: [...this.desiredRooms],
      sentAt: Date.now(),
      visible,
    });
  }

  private emitInstanceRegister(
    socket: Socket,
    appType: InstanceInfo["appType"],
    label: string
  ): void {
    socket.emit(BRIDGE_EVENTS.INSTANCE_REGISTER, {
      appType,
      instanceId: this.instanceId,
      label,
    });
  }

  private clearConnectRetryTimeout(): void {
    if (this.connectRetryTimeout) {
      clearTimeout(this.connectRetryTimeout);
      this.connectRetryTimeout = null;
    }
  }

  private disposeSocket(socket: Socket): void {
    if (this.socket === socket) {
      this.socket = null;
    }

    socket.removeAllListeners();
    socket.io.removeAllListeners();
    socket.disconnect();
  }

  private restartConnectionAttempt(
    socket: Socket,
    attemptId: number,
    delayMs?: number
  ): void {
    this.disposeSocket(socket);
    this.bridgeToken = null;
    const shouldReconnect = Boolean(this.shouldReconnect);
    this.setStatus(shouldReconnect ? "reconnecting" : "disconnected");
    if (!shouldReconnect) {
      return;
    }

    this.scheduleReconnectAttempt(attemptId, delayMs);
  }

  private scheduleReconnectAttempt(attemptId: number, delayMs?: number): void {
    if (!this.isConnectAttemptActive(attemptId)) {
      return;
    }

    if (
      this.socket ||
      this.connectRetryTimeout ||
      !(this.appType && this.label)
    ) {
      return;
    }

    const retryAfterMs = delayMs ?? this.retryDelayMs;

    if (delayMs === undefined) {
      this.retryDelayMs = Math.min(
        this.retryDelayMs * 2,
        BRIDGE_CONFIG.RECONNECT_MAX_DELAY
      );
    }

    this.connectRetryTimeout = setTimeout(() => {
      this.connectRetryTimeout = null;
      if (!this.isConnectAttemptActive(attemptId)) {
        return;
      }
      this.startConnectAttempt("reconnecting");
    }, retryAfterMs);
  }
}
